import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchClients, fetchClientIntegrations, PLATFORM_META } from "@/lib/api";
import { CheckCircle2, Circle, ArrowUpRight } from "lucide-react";

const PLATFORMS_ORDER = ["metricool", "semrush", "search_console", "google_analytics", "skylead", "google_sheets"];

export default function Integrations() {
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: fetchClients });

  return (
    <div className="space-y-8" data-testid="integrations-page">
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Connection matrix</div>
        <h2 className="font-display text-lg font-semibold text-slate-900 mt-0.5">Source availability across clients</h2>
        <p className="text-xs text-slate-500 mt-1">
          Each cell shows whether a client is connected to the platform. Click a client to manage integrations.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              <th className="px-5 py-3 sticky left-0 bg-slate-50">Client</th>
              {PLATFORMS_ORDER.map((p) => (
                <th key={p} className="px-4 py-3 text-center">{PLATFORM_META[p]?.name || p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <ClientRow key={c.id} client={c} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ClientRow({ client }) {
  const { data: integrations = [] } = useQuery({
    queryKey: ["client-integrations", client.id],
    queryFn: () => fetchClientIntegrations(client.id),
  });
  const byPlat = Object.fromEntries(integrations.map((i) => [i.platform, i]));

  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50" data-testid={`integrations-row-${client.id}`}>
      <td className="px-5 py-3 sticky left-0 bg-white">
        <Link to={`/clients/${client.id}`} className="flex items-center gap-3 group">
          <div
            className="h-8 w-8 rounded-md flex items-center justify-center font-display font-semibold text-white text-xs"
            style={{ background: client.color }}
          >
            {client.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          <div>
            <div className="text-sm font-medium text-slate-900 group-hover:underline">{client.name}</div>
            <div className="text-xs text-slate-500">{client.industry}</div>
          </div>
          <ArrowUpRight className="h-3.5 w-3.5 text-slate-400" />
        </Link>
      </td>
      {PLATFORMS_ORDER.map((p) => {
        const integ = byPlat[p];
        const connected = integ?.connected;
        return (
          <td key={p} className="px-4 py-3 text-center" data-testid={`integ-cell-${client.id}-${p}`}>
            {connected ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm">
                <CheckCircle2 className="h-3 w-3" /> On
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2 py-1 rounded-sm">
                <Circle className="h-3 w-3" /> Off
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}
